import { Checkbox, Grid, makeStyles, Typography } from '@material-ui/core';
import { CropOriginal, HelpOutline } from '@material-ui/icons';
import clsx from 'clsx';
import i18n from 'i18n';
import { CustomFields } from 'modules/partners/interfaces';
import React from 'react';
import { isValidUrl } from 'utils/helpers';
import { InitialValues } from '../initiValues';
import styles from './styles';

const useStyles = makeStyles(styles);
interface CustomImagePreviewProps {
  additionalButton?: React.ReactNode;
  helperDescripton?: string;
  imageId: string;
  label?: string;
  selector: boolean;
  values: CustomFields;
  handleOpenImage: (imageURL: string, description?: string | undefined) => void;
  handleRemoveImage: (idToRemove: string) => number | string[];
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void;
}
const CustomImagePreview = ({
  additionalButton,
  helperDescripton,
  imageId,
  label,
  selector,
  values,
  handleOpenImage,
  handleRemoveImage,
  setFieldValue,
}: CustomImagePreviewProps) => {
  const classes = useStyles();
  const [previewUrl, setPreviewUrl] = React.useState<
    string | ArrayBuffer | null
  >(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (
      typeof values[imageId] === 'string' &&
      values[imageId] !== InitialValues[imageId]
    ) {
      const url = `${process?.env?.REACT_APP_PUBLIC_BUCKET_URL}/${values[imageId]}`;

      if (isValidUrl(url)) {
        setPreviewUrl(url);
      }
    }
  }, [values, imageId]);

  const handleImageChange = (file: File) => {
    setFieldValue(imageId, file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleImageChange(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleClick = () => {
    if (inputRef?.current !== null) {
      inputRef.current.click();
    }
  };

  const handleDragLeave = () => setIsDragging(false);

  return (
    <Grid item>
      <Grid item className={classes.imageTitle}>
        {selector && (
          <Checkbox
            className={classes.checkbox}
            id={`checkbox-${imageId}`}
            value={imageId}
            onChange={() => handleRemoveImage(imageId)}
          />
        )}
        {label && (
          <>
            <Typography className={classes.imageTitle}>
              {i18n.t(label)}
            </Typography>
            <HelpOutline
              className={classes.helpIconImage}
              onClick={() =>
                handleOpenImage(
                  `/images/customExamples/${imageId}.png`,
                  helperDescripton
                )
              }
            />
          </>
        )}
      </Grid>

      <Grid
        container
        alignItems="center"
        justify="center"
        direction="column"
        className={clsx(classes.previewBox, {
          [classes.activePreviewBox]: isDragging || previewUrl,
        })}
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <input
          type="file"
          ref={inputRef}
          id={imageId}
          name={imageId}
          className={classes.inputHidden}
          accept="image/png, image/jpeg"
          onChange={event => {
            const file = event.target.files?.[0];
            if (file) {
              handleImageChange(file);
            }
          }}
        />

        {previewUrl ? (
          <img src={String(previewUrl)} alt="preview" />
        ) : (
          <Grid className={classes.dragAndDropTextContainer}>
            <CropOriginal />
            <Typography>{i18n.t('PARTNER_PAGE.DRAG_AND_DROP_KEY')}</Typography>
          </Grid>
        )}

        {additionalButton && (
          <Grid className={classes.additionalButton}>
            <Typography>{i18n.t('OR_KEY')}</Typography>
            {additionalButton}
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default CustomImagePreview;
